import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { toast } from 'react-toastify';
import {
  fetchBootstrap,
  fetchAudit,
  acquireLock,
  releaseLock,
  fetchLocks,
  fetchStates,
  saveFollowUp,
  extractEmails,
  type FollowUpRecord,
  type FollowUpState,
  type Lock,
  type AuditEntry,
} from '../services/followup';
import {
  buildCallListCsv,
  buildContractsCsv,
  downloadCsv,
  todayStamp,
  type FollowUpExportRow,
} from '../utils/followupExport';

// ---------- Constants ----------

const LOCK_TTL_MINUTES = 10;

const STATUS_OPTIONS = [
  { value: 'UNWORKED', label: 'Unworked' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'LEFT_VOICEMAIL', label: 'Called – left voicemail' },
  { value: 'NO_ANSWER', label: 'Called – no answer' },
  { value: 'EMAILED', label: 'Emailed client' },
  { value: 'CALLBACK_SET', label: 'Callback scheduled' },
  { value: 'CONTACT_INCORRECT', label: 'Contact data incorrect' },
  { value: 'ESCALATED', label: 'Escalated to manager/partner' },
  { value: 'COMPLETED_SIGNED', label: 'Completed – signed' },
] as const;

function getStatusLabel(value: string): string {
  return STATUS_OPTIONS.find((s) => s.value === value)?.label ?? (value || 'Unworked');
}

function statusPillClass(value: string): string {
  switch (value) {
    case 'COMPLETED_SIGNED':
      return 'good';
    case 'CONTACT_INCORRECT':
      return 'bad';
    case 'ESCALATED':
      return 'warn';
    default:
      return '';
  }
}

function contractStatusPillClass(status: string): string {
  const s = (status || '').toUpperCase();
  if (s === 'OPENED') return 'good';
  if (s === 'NOT_OPENED') return 'bad';
  return '';
}

// ---------- Helpers ----------

function formatTs(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(dateStr: string): string {
  const s = (dateStr || '').trim();
  if (!s || s.startsWith('1970-01-01')) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

function formatCurrency(value: number | string): string {
  const n = Number(value);
  if (!isFinite(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function lockIsStale(lock: Lock | null): boolean {
  if (!lock?.ts) return true;
  const ts = new Date(lock.ts);
  if (isNaN(ts.getTime())) return true;
  return Date.now() - ts.getTime() > LOCK_TTL_MINUTES * 60 * 1000;
}

function normalizeLock(raw: any): Lock | null {
  if (!raw || typeof raw !== 'object') return null;
  return {
    user: String(raw.user ?? raw.USER ?? ''),
    fullname: String(raw.fullname ?? raw.FULLNAME ?? ''),
    ts: String(raw.ts ?? raw.TS ?? raw.timestamp ?? ''),
  };
}

// ---------- Component ----------

type RecordWithId = FollowUpRecord & { _id: string };

interface Drafts {
  [id: string]: { status: string; notes: string };
}

interface UIState {
  search: string;
  office: string;
  status: string;
  sortDir: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export default function FollowUpHub() {
  const { email } = useAuth();

  // User identity for lock/audit
  const activeUser = useMemo(
    () => (email ? { user: email, fullname: email } : null),
    [email],
  );

  // Core data
  const [records, setRecords] = useState<RecordWithId[]>([]);
  const [states, setStates] = useState<Record<string, FollowUpState>>({});
  const [locks, setLocks] = useState<Record<string, Lock>>({});
  const [drafts, setDrafts] = useState<Drafts>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // UI
  const [ui, setUi] = useState<UIState>({
    search: '',
    office: 'ALL',
    status: 'ALL',
    sortDir: 'desc',
    page: 1,
    pageSize: 50,
  });

  const [loading, setLoading] = useState(true);
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const pendingLocks = useRef(new Map<string, Promise<boolean>>());

  // Audit modal
  const [auditKey, setAuditKey] = useState<string | null>(null);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditRecord, setAuditRecord] = useState<RecordWithId | null>(null);

  // ---------- Bootstrap ----------

  const doBootstrap = useCallback(async () => {
    try {
      const data = await fetchBootstrap();
      const recs = (data.records || []).map((r) => ({
        ...r,
        _id: String(r.PRIMARY_KEY),
      }));
      setRecords(recs);
      setStates(data.states && typeof data.states === 'object' ? data.states : {});
      setLocks(data.locks && typeof data.locks === 'object' ? data.locks : {});
    } catch (err: any) {
      toast.error('Failed to load follow-up data: ' + (err?.message || ''));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    doBootstrap();
  }, [doBootstrap]);

  // ---------- Polling ----------

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const [newLocks, newStates] = await Promise.all([fetchLocks(), fetchStates()]);
        setLocks(newLocks);
        setStates(newStates);
      } catch {
        // silent
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // ---------- Lock helpers ----------

  const getLock = useCallback(
    (id: string): Lock | null => {
      const lock = locks[id];
      if (!lock) return null;
      return lockIsStale(lock) ? null : lock;
    },
    [locks],
  );

  const canEdit = useCallback(
    (id: string): boolean => {
      const lock = getLock(id);
      if (!lock) return true;
      if (!activeUser) return false;
      return lock.user === activeUser.user;
    },
    [getLock, activeUser],
  );

  const ensureLock = useCallback(
    async (id: string, force = false): Promise<boolean> => {
      if (!activeUser) return false;

      // Fast path: already have a valid lock
      const current = locks[id];
      if (!force && current && !lockIsStale(current) && current.user === activeUser.user) {
        return true;
      }

      // Deduplicate in-flight requests
      if (pendingLocks.current.has(id)) {
        return pendingLocks.current.get(id)!;
      }

      const p = (async () => {
        try {
          const res = await acquireLock(id, LOCK_TTL_MINUTES);

          setLocks((prev) => {
            const next = { ...prev };
            if (res.locks && typeof res.locks === 'object') {
              return res.locks as Record<string, Lock>;
            }
            if (res.lock) {
              const l = normalizeLock(res.lock);
              if (l) next[id] = l;
            }
            return next;
          });

          return !!res.ok;
        } catch {
          return false;
        }
      })();

      pendingLocks.current.set(id, p);
      try {
        return await p;
      } finally {
        pendingLocks.current.delete(id);
      }
    },
    [activeUser, locks],
  );

  const doReleaseLock = useCallback(
    async (id: string) => {
      if (!activeUser) return;
      const existing = locks[id];
      if (!existing || (existing.user && existing.user !== activeUser.user)) return;

      try {
        await releaseLock(id);
        setLocks((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      } catch {
        // silent
      }
    },
    [activeUser, locks],
  );

  // ---------- Effective state ----------

  const getEffectiveStatus = useCallback(
    (id: string): string => {
      const d = drafts[id];
      if (d?.status) return d.status;
      return states[id]?.status || 'UNWORKED';
    },
    [drafts, states],
  );

  const getEffectiveNotes = useCallback(
    (id: string): string => {
      const d = drafts[id];
      if (d && typeof d.notes === 'string') return d.notes;
      return states[id]?.notes ?? '';
    },
    [drafts, states],
  );

  const hasDraft = useCallback(
    (id: string): boolean => {
      const d = drafts[id];
      if (!d) return false;
      const saved = states[id];
      return (
        (d.status ?? 'UNWORKED') !== (saved?.status ?? 'UNWORKED') ||
        (d.notes ?? '') !== (saved?.notes ?? '')
      );
    },
    [drafts, states],
  );

  // ---------- Filter & Sort ----------

  const offices = useMemo(() => {
    const set = new Set(records.map((r) => (r.CLIENTOFFICE || '—').trim() || '—'));
    return ['ALL', ...Array.from(set).sort()];
  }, [records]);

  const filtered = useMemo(() => {
    const search = ui.search.toLowerCase();
    let out = records.filter((r) => {
      if (ui.office !== 'ALL') {
        if (((r.CLIENTOFFICE || '—').trim() || '—') !== ui.office) return false;
      }
      if (ui.status !== 'ALL') {
        if (getEffectiveStatus(r._id) !== ui.status) return false;
      }
      if (search) {
        const hay = [
          r.GROUP_ID,
          r.GROUP_NAME,
          r.CLIENT_CONTACT_NAME,
          r.CLIENTPARTNER,
          r.CLIENTMANAGER,
          r.PARTY_EMAIL,
          r.CLIENTOFFICE,
        ]
          .map((x) => String(x || ''))
          .join(' ')
          .toLowerCase();
        if (!hay.includes(search)) return false;
      }
      return true;
    });

    out.sort((a, b) => {
      const dir = ui.sortDir === 'asc' ? 1 : -1;
      return ((Number(a.CY_WIP) || 0) - (Number(b.CY_WIP) || 0)) * dir;
    });

    return out;
  }, [records, ui.search, ui.office, ui.status, ui.sortDir, getEffectiveStatus]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / ui.pageSize));
  const clampedPage = Math.min(Math.max(1, ui.page), pageCount);
  const pageStart = (clampedPage - 1) * ui.pageSize;
  const pageEnd = pageStart + ui.pageSize;
  const visible = filtered.slice(pageStart, pageEnd);

  // ---------- Handlers ----------

  const handleDraftChange = useCallback(
    (id: string, field: 'status' | 'notes', value: string) => {
      setDrafts((prev) => ({
        ...prev,
        [id]: {
          status: prev[id]?.status ?? getEffectiveStatus(id),
          notes: prev[id]?.notes ?? getEffectiveNotes(id),
          [field]: value,
        },
      }));
    },
    [getEffectiveStatus, getEffectiveNotes],
  );

  const handleFocus = useCallback(
    async (id: string) => {
      if (!activeUser) return;
      const ok = await ensureLock(id);
      if (!ok) {
        const lock = getLock(id);
        const who = lock ? `${lock.fullname} (${lock.user})` : 'another user';
        toast.error(`Locked by ${who}`);
      }
    },
    [activeUser, ensureLock, getLock],
  );

  const handleSave = useCallback(
    async (id: string) => {
      if (!activeUser) {
        toast.error('Not authenticated.');
        return;
      }
      if (savingIds.has(id)) return;

      const ok = await ensureLock(id);
      if (!ok) {
        const lock = getLock(id);
        const who = lock ? `${lock.fullname} (${lock.user})` : 'another user';
        toast.error(`Can't save: locked by ${who}`);
        return;
      }

      setSavingIds((prev) => new Set(prev).add(id));
      try {
        const status = getEffectiveStatus(id);
        const notes = getEffectiveNotes(id);

        await saveFollowUp(id, status, notes);

        // Update local states optimistically
        setStates((prev) => ({
          ...prev,
          [id]: {
            status,
            notes,
            updatedAt: new Date().toISOString(),
            updatedBy: { user: activeUser.user, fullname: activeUser.fullname },
          },
        }));

        // Clear draft
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });

        await doReleaseLock(id);

        // Refresh states from server
        try {
          const newStates = await fetchStates();
          setStates(newStates);
        } catch {
          // keep optimistic state
        }

        toast.success('Saved.');
      } catch (err: any) {
        toast.error('Save failed: ' + (err?.message || ''));
      } finally {
        setSavingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [activeUser, savingIds, ensureLock, getLock, getEffectiveStatus, getEffectiveNotes, doReleaseLock],
  );

  // const handleTakeover = useCallback(
  //   async (id: string) => {
  //     if (!activeUser) return;
  //     const ok = await ensureLock(id, true);
  //     if (ok) toast.success('Row checked out to you (forced).');
  //   },
  //   [activeUser, ensureLock],
  // );

  // ---------- Export ----------

  // Exports every row matching the current filters, not just the visible page,
  // and reads through drafts so unsaved edits aren't silently dropped.
  const buildExportRows = useCallback(
    (): FollowUpExportRow[] =>
      filtered.map((r) => {
        const meta = states[r._id];
        const statusCode = getEffectiveStatus(r._id);
        return {
          record: r,
          statusCode,
          statusLabel: getStatusLabel(statusCode),
          notes: getEffectiveNotes(r._id),
          updatedAt: meta?.updatedAt ?? '',
          updatedByName: meta?.updatedBy?.fullname ?? '',
          updatedByEmail: meta?.updatedBy?.user ?? '',
        };
      }),
    [filtered, states, getEffectiveStatus, getEffectiveNotes],
  );

  const handleExport = useCallback(
    (shape: 'callList' | 'contracts') => {
      const rows = buildExportRows();
      if (rows.length === 0) {
        toast.error('Nothing to export — no records match your filters.');
        return;
      }
      const csv = shape === 'callList' ? buildCallListCsv(rows) : buildContractsCsv(rows);
      const label = shape === 'callList' ? 'call-list' : 'contracts';
      downloadCsv(csv, `followup-hub-${label}-${todayStamp()}.csv`);
      toast.success(`Exported ${rows.length} ${rows.length === 1 ? 'client' : 'clients'}.`);
    },
    [buildExportRows],
  );

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSort = useCallback(() => {
    setUi((prev) => ({
      ...prev,
      sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  }, []);

  // ---------- Audit modal ----------

  const openAudit = useCallback(
    async (id: string) => {
      const r = records.find((x) => x._id === id) || null;
      setAuditRecord(r);
      setAuditKey(id);
      setAuditLoading(true);
      setAuditEntries([]);

      try {
        const entries = await fetchAudit(id);
        setAuditEntries(entries);
      } catch {
        toast.error('Failed to load audit trail.');
      } finally {
        setAuditLoading(false);
      }
    },
    [records],
  );

  const closeAudit = useCallback(() => {
    setAuditKey(null);
    setAuditEntries([]);
    setAuditRecord(null);
  }, []);

  // ---------- Render ----------

  if (loading) {
    return (
      <div className="page-stack" style={{ padding: 14 }}>
        <div className="card">
          <div className="bd" style={{ textAlign: 'center', padding: 40 }}>
            <div className="muted">Loading follow-up data...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-stack" style={{ padding: 14 }}>
      <div className="card">
        <div className="hd">
          <div className="row">
            <div>
              <div style={{ fontWeight: 760, fontSize: 16 }}>
                Follow-Up Hub — Unsigned Contract Call Queue
              </div>
              <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                Root-level rows show <strong>who to contact</strong>. Expand to see{' '}
                <strong>open contracts</strong>. Status &amp; notes are saved to the server.
              </div>
            </div>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <span className="badge">{email}</span>
              <button
                className="btn"
                disabled={filtered.length === 0}
                title="One row per client: contact info, phones, follow-up status, notes, WIP, contract counts"
                onClick={() => handleExport('callList')}
              >
                Export CSV{filtered.length ? ` (${filtered.length})` : ''}
              </button>
              <button
                className="btn"
                disabled={filtered.length === 0}
                title="One row per open contract, with the client + contact + status repeated"
                onClick={() => handleExport('contracts')}
              >
                Export Contracts
              </button>
              <button
                className="btn"
                onClick={() => {
                  setLoading(true);
                  doBootstrap();
                }}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div style={{ padding: '10px 14px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div className="muted" style={{ fontSize: 11, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>Search</div>
              <input
                placeholder="Group ID, name, contact, email..."
                value={ui.search}
                onChange={(e) => setUi((p) => ({ ...p, search: e.target.value, page: 1 }))}
                style={{ minWidth: 240 }}
              />
            </div>
            <div>
              <div className="muted" style={{ fontSize: 11, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>Office</div>
              <select value={ui.office} onChange={(e) => setUi((p) => ({ ...p, office: e.target.value, page: 1 }))}>
                {offices.map((o) => (
                  <option key={o} value={o}>
                    {o === 'ALL' ? 'All offices' : o}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 11, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>Status</div>
              <select value={ui.status} onChange={(e) => setUi((p) => ({ ...p, status: e.target.value, page: 1 }))}>
                <option value="ALL">All statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="badge" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {filtered.length === 0
                ? `0 / ${records.length}`
                : `${pageStart + 1}–${Math.min(pageEnd, filtered.length)} / ${filtered.length}`}
            </span>
            <select
              value={ui.pageSize}
              onChange={(e) => setUi((p) => ({ ...p, pageSize: Number(e.target.value), page: 1 }))}
              style={{ width: 'auto', minWidth: 100 }}
            >
              {[25, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} rows
                </option>
              ))}
            </select>
            <button className="btn" disabled={clampedPage <= 1} onClick={() => setUi((p) => ({ ...p, page: p.page - 1 }))}>
              Prev
            </button>
            <span className="badge" style={{ fontVariantNumeric: 'tabular-nums' }}>
              Page {clampedPage} / {pageCount}
            </span>
            <button className="btn" disabled={clampedPage >= pageCount} onClick={() => setUi((p) => ({ ...p, page: p.page + 1 }))}>
              Next
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto', padding: '0 14px 14px' }}>
          <table className="viewer-table" style={{ minWidth: 1100 }}>
            <thead>
              <tr>
                <th style={{ width: 44 }}></th>
                <th>Group</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={toggleSort}>
                  CY WIP {ui.sortDir === 'asc' ? '▲' : '▼'}
                </th>
                <th>Office</th>
                <th>Management</th>
                <th>Contact</th>
                <th style={{ minWidth: 340 }}>Workflow</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 24 }}>
                    <span className="muted">No records match your filters.</span>
                  </td>
                </tr>
              )}
              {visible.map((r) => {
                const id = r._id;
                const contracts = Array.isArray(r.OPEN_CONTRACTS) ? r.OPEN_CONTRACTS : [];
                const lock = getLock(id);
                const isMe = activeUser && lock?.user === activeUser.user;
                const editable = !!activeUser && canEdit(id);
                const statusVal = getEffectiveStatus(id);
                const notesVal = getEffectiveNotes(id);
                const meta = states[id];
                const isExpanded = expanded.has(id);
                const saving = savingIds.has(id);
                const dirty = hasDraft(id);
                const emails = extractEmails(r.PARTY_EMAIL || '');
                const phones = r.PHONENUMBERS && typeof r.PHONENUMBERS === 'object' ? r.PHONENUMBERS : {};

                return (
                  <React.Fragment key={id}>
                    {/* Main row */}
                    <tr>
                      <td>
                        <button
                          className="btn"
                          style={{ padding: '6px 10px', fontSize: 16, fontWeight: 900 }}
                          onClick={() => toggleExpanded(id)}
                          title="Toggle contracts"
                        >
                          <span style={{ display: 'inline-block', transition: 'transform .14s', transform: isExpanded ? 'rotate(90deg)' : 'none' }}>›</span>
                        </button>
                      </td>

                      {/* Group */}
                      <td>
                        <div style={{ minWidth: 220 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <span style={{ fontWeight: 800, fontSize: 13 }}>{r.GROUP_ID || '—'}</span>
                            <span className="pill" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>{contracts.length}</span>
                          </div>
                          <div style={{ fontWeight: 700, marginTop: 2 }}>{r.GROUP_NAME || '—'}</div>
                        </div>
                      </td>

                      {/* CY WIP */}
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {formatCurrency(r.CY_WIP)}
                      </td>

                      {/* Office */}
                      <td>
                        <div style={{ fontWeight: 700 }}>{r.CLIENTOFFICE || '—'}</div>
                      </td>

                      {/* Management */}
                      <td>
                        <div>
                          <div style={{ fontWeight: 700 }}>{r.CLIENTPARTNER || '—'}</div>
                          <div className="muted" style={{ fontSize: 12 }}>Partner</div>
                          <div style={{ fontWeight: 700, marginTop: 6 }}>{r.CLIENTMANAGER || '—'}</div>
                          <div className="muted" style={{ fontSize: 12 }}>Manager</div>
                        </div>
                      </td>

                      {/* Contact */}
                      <td>
                        <div style={{ minWidth: 180 }}>
                          <div style={{ fontWeight: 700 }}>{r.CLIENT_CONTACT_NAME || '—'}</div>
                          <div className="muted" style={{ fontSize: 13 }}>
                            {emails.length > 0
                              ? emails.map((e) => (
                                  <div key={e}>
                                    <a href={`mailto:${e}`} style={{ color: 'var(--accent)' }}>
                                      {e}
                                    </a>
                                  </div>
                                ))
                              : '—'}
                          </div>
                          <div className="muted" style={{ fontSize: 13 }}>
                            {Object.entries(phones).map(([k, v]) =>
                              v ? (
                                <div key={k}>
                                  {k}: {String(v)}
                                </div>
                              ) : null,
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Workflow */}
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 300 }}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180 }}>
                              <span className={`pill ${statusPillClass(statusVal)}`}>
                                {getStatusLabel(statusVal)}
                              </span>
                              <select
                                value={statusVal}
                                disabled={!editable}
                                onChange={(e) => handleDraftChange(id, 'status', e.target.value)}
                                onFocus={() => handleFocus(id)}
                                style={{ fontSize: 13 }}
                              >
                                {STATUS_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <span className="muted" style={{ fontSize: 12 }}>Notes</span>
                              <textarea
                                value={notesVal}
                                disabled={!editable}
                                placeholder="e.g. Attempted to call..."
                                onChange={(e) => handleDraftChange(id, 'notes', e.target.value)}
                                onFocus={() => handleFocus(id)}
                                style={{
                                  border: '1px solid var(--border)',
                                  background: 'rgba(0,0,0,.18)',
                                  color: 'var(--text)',
                                  padding: '8px 10px',
                                  borderRadius: 10,
                                  fontSize: 13,
                                  resize: 'vertical',
                                  minHeight: 36,
                                  fontFamily: 'inherit',
                                }}
                              />
                            </div>
                          </div>

                          {/* Lock line + actions */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              {lock ? (
                                <span
                                  className="muted"
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: isMe ? 'var(--good)' : 'var(--bad)',
                                  }}
                                >
                                  ● Locked by {lock.fullname} ({lock.user}) · {formatTs(lock.ts)}
                                </span>
                              ) : (
                                <span className="muted" style={{ fontSize: 12 }}>● Available</span>
                              )}
                              {/* {lock && activeUser && lock.user !== activeUser.user && (
                                <button
                                  className="btn"
                                  style={{ fontSize: 12, padding: '4px 8px' }}
                                  onClick={() => handleTakeover(id)}
                                >
                                  Take over
                                </button>
                              )} */}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                className="btn"
                                style={{ fontSize: 12, padding: '4px 8px' }}
                                onClick={() => openAudit(id)}
                              >
                                Audit
                              </button>
                              <button
                                className="btn primary"
                                style={{ fontSize: 12, padding: '4px 8px' }}
                                disabled={!editable || !dirty || saving}
                                onClick={() => handleSave(id)}
                              >
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </div>

                          {/* Last saved */}
                          <div className="muted" style={{ fontSize: 12 }}>
                            {meta?.updatedAt && meta?.updatedBy
                              ? `Last saved: ${formatTs(meta.updatedAt)} by ${meta.updatedBy.fullname} (${meta.updatedBy.user})`
                              : 'No saved updates yet.'}
                          </div>
                        </div>
                      </td>
                    </tr>

                    {/* Expandable detail row */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0, background: 'rgba(0,0,0,.08)' }}>
                          <div style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                              <div style={{ fontWeight: 800, fontSize: 14 }}>
                                Open contracts for {r.GROUP_ID || '—'}
                              </div>
                              <span className="pill" style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                                {contracts.length} open
                              </span>
                            </div>
                            {contracts.length === 0 ? (
                              <div className="muted" style={{ padding: 12 }}>No open contracts listed.</div>
                            ) : (
                              <table className="viewer-table">
                                <thead>
                                  <tr>
                                    <th style={{ width: '68%' }}>Contract Title</th>
                                    <th style={{ width: '16%' }}>Status</th>
                                    <th style={{ width: '16%' }}>Delivered</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {contracts.map((c, ci) => (
                                    <tr key={ci}>
                                      <td style={{ fontWeight: 700 }}>{c['Contract Title'] || '—'}</td>
                                      <td>
                                        <span className={`pill ${contractStatusPillClass(c['Contract Status'] || '')}`}>
                                          {c['Contract Status'] || '—'}
                                        </span>
                                      </td>
                                      <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                        {formatDate(c['Contract Delivered'] || '')}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Audit Modal */}
      {auditKey && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 60,
            padding: 18,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeAudit();
          }}
        >
          <div
            style={{
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 16,
              padding: 18,
              maxWidth: 680,
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 40px rgba(0,0,0,.5)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Audit Trail</div>
              <button className="btn" style={{ padding: '4px 10px' }} onClick={closeAudit}>
                ✕
              </button>
            </div>

            {auditRecord && (
              <div className="muted" style={{ fontSize: 13, marginBottom: 12, whiteSpace: 'pre-wrap' }}>
                {`Group: ${auditRecord.GROUP_ID || '—'}\nName: ${auditRecord.GROUP_NAME || '—'}\nContact: ${auditRecord.CLIENT_CONTACT_NAME || '—'}`}
              </div>
            )}

            {auditLoading ? (
              <div className="muted" style={{ padding: 16 }}>Loading...</div>
            ) : auditEntries.length === 0 ? (
              <div className="muted" style={{ padding: 16 }}>No audit entries yet for this client.</div>
            ) : (
              <table className="viewer-table">
                <thead>
                  <tr>
                    <th style={{ width: '18%' }}>When</th>
                    <th style={{ width: '20%' }}>User</th>
                    <th style={{ width: '18%' }}>Status</th>
                    <th style={{ width: '44%' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {auditEntries.map((e, i) => (
                    <tr key={i}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatTs(e.ts)}</td>
                      <td>
                        {e.fullname} ({e.user})
                      </td>
                      <td>
                        <span className={`pill ${statusPillClass(e.status)}`}>
                          {getStatusLabel(e.status)}
                        </span>
                      </td>
                      <td>{e.notes || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={closeAudit}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
